import React, { useState } from 'react';
import { Target, Check, ArrowRight, Brain, Filter, ChevronRight } from 'lucide-react';
import { regions, DIFFICULTY_LEVELS, QUESTION_TYPES } from '../data/questionsData';
import { difficultyConfig, questionTypeConfig } from '../data/questionTypes';

const QuizSetup = ({ onStartQuiz, totalQuestions }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRegions, setSelectedRegions] = useState(['cabeza', 'cuello', 'torax', 'abdomen', 'espalda', 'pelvis', 'brazo', 'pierna']);
  const [selectedDifficulties, setSelectedDifficulties] = useState([DIFFICULTY_LEVELS.BASICO, DIFFICULTY_LEVELS.GENERAL, DIFFICULTY_LEVELS.COMPLEJO]);
  const [selectedTypes, setSelectedTypes] = useState(Object.values(QUESTION_TYPES));

  const getIconComponent = (iconName) => {
    const icons = {
      Brain,
      Target,
      Filter
    };
    return icons[iconName] || Brain;
  };

  const toggleRegion = (regionId) => {
    setSelectedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(r => r !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };

  const toggleDifficulty = (difficulty) => {
    setSelectedDifficulties(prev => {
      if (prev.includes(difficulty)) {
        return prev.filter(d => d !== difficulty);
      } else {
        return [...prev, difficulty];
      }
    });
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleNext = () => {
    console.log('Current step:', currentStep);
    console.log('Selected regions:', selectedRegions);
    console.log('Selected regions length:', selectedRegions.length);

    if (currentStep === 1 && selectedRegions.length === 0) {
      alert('Selecciona al menos una región');
      return;
    }
    if (currentStep === 2 && selectedDifficulties.length === 0) {
      alert('Selecciona al menos un nivel de dificultad');
      return;
    }
    if (currentStep === 3 && selectedTypes.length === 0) {
      alert('Selecciona al menos un tipo de pregunta');
      return;
    }

    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleStart = () => {
    onStartQuiz({
      regions: selectedRegions,
      difficulties: selectedDifficulties,
      types: selectedTypes
    });
  };

  const canProceed = () => {
    if (currentStep === 1) return selectedRegions.length > 0;
    if (currentStep === 2) return selectedDifficulties.length > 0;
    if (currentStep === 3) return selectedTypes.length > 0;
    return false;
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-4 px-3 sm:py-6 sm:px-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-white/80 rounded-2xl shadow-lg">
              <Brain className="w-10 h-10 text-purple-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">Configura tu Quiz</h1>
          </div>
          <p className="text-lg text-gray-600">Personaliza tu experiencia de aprendizaje</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          <div className="flex justify-center items-center mb-4 sm:mb-6 px-2">
            {[
              { step: 1, label: 'Regiones', icon: Target },
              { step: 2, label: 'Dificultad', icon: Brain },
              { step: 3, label: 'Tipos', icon: Filter }
            ].map(({ step, label, icon: Icon }, index) => (
              <div key={step} className="flex items-center mx-2">
                <div className={`flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-full font-bold border-3 transition-all shadow-lg ${
                  step === currentStep
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-300 scale-110'
                    : step < currentStep
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-green-300'
                    : 'bg-white text-gray-400 border-gray-300 hover:border-purple-400'
                }`}>
                  {step < currentStep ? <Check className="w-4 h-4 sm:w-6 sm:h-6" /> : <Icon className="w-4 h-4 sm:w-6 sm:h-6" />}
                </div>
                <div className="ml-2 sm:ml-3 hidden sm:block">
                  <div className={`font-semibold text-xs sm:text-sm ${
                    step === currentStep ? 'text-purple-700' : step < currentStep ? 'text-green-700' : 'text-gray-500'
                  }`}>
                    {label}
                  </div>
                </div>
                {step < 3 && (
                  <div className={`w-8 sm:w-16 h-0.5 mx-2 sm:mx-4 transition-all ${
                    step < currentStep ? 'bg-gradient-to-r from-green-400 to-emerald-400' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Mobile labels */}
          <div className="flex justify-center sm:hidden mb-4">
            <div className={`font-semibold text-sm transition-all ${
              currentStep === 1 ? 'text-purple-700' : currentStep === 2 ? 'text-green-700' : 'text-blue-700'
            }`}>
              {currentStep === 1 ? 'Regiones' : currentStep === 2 ? 'Dificultad' : 'Tipos'}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-10 border border-purple-100">
          {/* Step 1: Regions */}
          {currentStep === 1 && (
            <div className="min-h-[350px] sm:min-h-[400px] flex flex-col">
              <div className="mb-4 sm:mb-8 text-center">
                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center justify-center gap-2 sm:gap-3">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
                  Selecciona Regiones
                </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600">Elige las áreas anatómicas que deseas estudiar</p>
              </div>

              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8 px-1 sm:px-0 justify-items-center">
                {regions.map(region => {
                  const isSelected = selectedRegions.includes(region.id);
                  return (
                    <button
                      type="button"
                      key={region.id}
                      onClick={() => toggleRegion(region.id)}
                      className={`w-full max-w-[140px] sm:max-w-[180px] relative p-3 sm:p-6 rounded-xl sm:rounded-2xl border-3 transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                        isSelected
                          ? 'bg-gradient-to-br from-purple-100 to-pink-100 border-purple-400 text-purple-800 shadow-purple-200'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2 sm:gap-3">
                        <span className="text-2xl sm:text-4xl">{region.icon}</span>
                        <span className="font-semibold text-center text-xs sm:text-sm">{region.name}</span>
                        {isSelected && (
                          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-4 h-4 sm:w-6 sm:h-6 bg-purple-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-purple-50 px-3 sm:px-4 py-2 rounded-full border border-purple-200">
                  <Target className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                  <span className="text-xs sm:text-sm font-semibold text-purple-700">
                    {selectedRegions.length} {selectedRegions.length === 1 ? 'región seleccionada' : 'regiones seleccionadas'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Difficulty */}
          {currentStep === 2 && (
            <div className="min-h-[350px] sm:min-h-[400px] flex flex-col">
              <div className="mb-4 sm:mb-8 text-center">
                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center justify-center gap-2 sm:gap-3">
                  <Brain className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
                  Nivel de Dificultad
                </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600">Selecciona el nivel que prefieras</p>
              </div>

              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8 px-2 sm:px-0 justify-items-center">
                {Object.entries(difficultyConfig).map(([key, config]) => {
                  const isSelected = selectedDifficulties.includes(key);
                  const getDifficultyColor = () => {
                    const colors = {
                      green: 'from-green-100 to-emerald-100 border-green-400 text-green-800 shadow-green-200',
                      blue: 'from-blue-100 to-cyan-100 border-blue-400 text-blue-800 shadow-blue-200',
                      red: 'from-red-100 to-rose-100 border-red-400 text-red-800 shadow-red-200'
                    };
                    return colors[config.color] || 'from-gray-100 to-gray-200 border-gray-300 text-gray-800 shadow-gray-200';
                  };

                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => toggleDifficulty(key)}
                      className={`w-full max-w-[360px] sm:max-w-none mx-auto p-4 sm:p-8 rounded-xl sm:rounded-3xl border-3 transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                        isSelected
                          ? `bg-gradient-to-br ${getDifficultyColor()}`
                          : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                      }`}
                    >
                      <div className="text-center">
                        <span className="text-4xl sm:text-6xl mb-2 sm:mb-4 block">{config.icon}</span>
                        <h3 className="font-bold text-base sm:text-xl mb-1 sm:mb-2">{config.label}</h3>
                        <p className="text-xs sm:text-sm opacity-80 mb-2 sm:mb-4">{config.description}</p>
                        {isSelected && (
                          <div className="inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-current rounded-full">
                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-purple-50 px-3 sm:px-4 py-2 rounded-full border border-purple-200">
                  <Brain className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                  <span className="text-xs sm:text-sm font-semibold text-purple-700">
                    {selectedDifficulties.length} {selectedDifficulties.length === 1 ? 'nivel seleccionado' : 'niveles seleccionados'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Question Types */}
          {currentStep === 3 && (
            <div className="min-h-[350px] sm:min-h-[400px] flex flex-col">
              <div className="mb-4 sm:mb-8 text-center">
                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center justify-center gap-2 sm:gap-3">
                  <Filter className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
                  Tipos de Preguntas
                </h2>
                <p className="text-sm sm:text-base md:text-lg text-gray-600">Elige los tipos que quieres practicar</p>
              </div>

              <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8 px-1 sm:px-0 justify-items-center">
                {Object.entries(questionTypeConfig).map(([key, config]) => {
                  const isSelected = selectedTypes.includes(key);
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => toggleType(key)}
                      className={`w-full max-w-[140px] sm:max-w-[180px] p-3 sm:p-6 rounded-xl sm:rounded-2xl border-3 transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                        isSelected
                          ? 'bg-gradient-to-br from-blue-100 to-cyan-100 border-blue-400 text-blue-800 shadow-blue-200'
                          : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2 sm:gap-3">
                        <span className="text-3xl sm:text-5xl">{config.icon}</span>
                        <span className="font-semibold text-center text-xs sm:text-sm">{config.label}</span>
                        {isSelected && (
                          <div className="mt-1 sm:mt-2 inline-flex items-center justify-center w-4 h-4 sm:w-6 sm:h-6 bg-blue-500 rounded-full">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-purple-50 px-3 sm:px-4 py-2 rounded-full border border-purple-200">
                  <Filter className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                  <span className="text-xs sm:text-sm font-semibold text-purple-700">
                    {selectedTypes.length} {selectedTypes.length === 1 ? 'tipo seleccionado' : 'tipos seleccionados'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 sm:gap-4 mt-6 sm:mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-lg transition-all shadow-lg hover:shadow-xl"
              >
                ← Atrás
              </button>
            )}
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 sm:w-6 sm:h-6" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStart}
                disabled={!canProceed()}
                className="flex-1 flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-lg shadow-lg hover:shadow-xl transition-all"
              >
                <Brain className="w-4 h-4 sm:w-6 sm:h-6" />
                ¡Comenzar Quiz!
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizSetup;
